import { useAlertContext } from "@shared";
import { getUploadErrorMessage } from "@shared";
import { useQueryClient } from "@tanstack/react-query";

import { AssetInventoryUploadArea } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/assetInventoryUploadAreas";
import { useTranslation } from "@/translations";

/**
 * Deferred-save upload into an asset inventory upload area, shared by the owning views
 * (Persönliche Daten, Angehörige) and the documents overview: every file is uploaded
 * independently via the area's registry entry, failures are reported per file and returned so
 * the caller keeps them pending in its form, and the area's document list is refreshed.
 * Single implementation so a new upload area only needs its upload-area registry entry.
 */
export function useAssetInventoryUpload(clientId: string) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const queryClient = useQueryClient();

  return async (area: AssetInventoryUploadArea, files: File[]): Promise<File[]> => {
    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          await area.uploadDocument(clientId, file);
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [area.listQueryKey, clientId] });
    return failed;
  };
}
